'use client'

import { cn } from '@/lib/utils'
import type { MarketStatus, Platform } from '@/types/market'
import { AlertTriangle, CheckCircle2, Circle, Lock, Sparkles, XCircle } from 'lucide-react'

type StatusBadgeProps = {
	status: MarketStatus
	size?: 'sm' | 'md'
	showLabel?: boolean
}

const STATUS_CONFIG: Record<
	MarketStatus,
	{ label: string; color: string; icon: typeof CheckCircle2; bgColor: string; iconColor: string }
> = {
	active: {
		label: 'Active',
		color: 'text-emerald-400',
		bgColor: 'bg-emerald-500/10',
		iconColor: 'text-emerald-400',
		icon: CheckCircle2,
	},
	closed: {
		label: 'Closed',
		color: 'text-muted-foreground',
		bgColor: 'bg-muted',
		iconColor: 'text-muted-foreground',
		icon: Lock,
	},
}

export function StatusBadge({ status, size = 'sm', showLabel = true }: StatusBadgeProps) {
	const config = STATUS_CONFIG[status]
	const Icon = config.icon

	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 rounded-full font-medium',
				config.color,
				config.bgColor,
				size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'
			)}
		>
			<Icon className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
			{showLabel && config.label}
		</span>
	)
}

// Icon-only status indicator for compact table views
type StatusIconProps = {
	status: MarketStatus
	size?: 'sm' | 'md'
}

export function StatusIcon({ status, size = 'sm' }: StatusIconProps) {
	const config = STATUS_CONFIG[status]
	const Icon = config.icon

	return (
		<span title={config.label}>
			<Icon className={cn(config.iconColor, size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
		</span>
	)
}

// Tradability icon-only indicator for compact table views
type TradabilityIconProps = {
	isTradable: boolean
	platform: Platform
	size?: 'sm' | 'md'
}

export function TradabilityIcon({ isTradable, platform, size = 'sm' }: TradabilityIconProps) {
	// Only show for non-tradable Kalshi markets
	if (isTradable || platform !== 'kalshi') {
		return (
			<span title="Available onchain">
				<Circle
					className={cn('text-emerald-400', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')}
					fill="currentColor"
				/>
			</span>
		)
	}

	return (
		<span title="Not available onchain - trade on Kalshi.com">
			<AlertTriangle className={cn('text-amber-400', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
		</span>
	)
}

type TradabilityIndicatorProps = {
	isTradable: boolean
	platform: Platform
	size?: 'sm' | 'md'
	showLabel?: boolean
}

export function TradabilityIndicator({
	isTradable,
	platform,
	size = 'sm',
	showLabel = false,
}: TradabilityIndicatorProps) {
	if (isTradable) {
		return null // Don't show anything if tradable (default state)
	}

	// Only Kalshi markets can be non-tradable onchain
	if (platform !== 'kalshi') {
		return null
	}

	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 rounded-full font-medium',
				'bg-amber-500/10 text-amber-400',
				size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'
			)}
			title="Not available for onchain trading. Trade on Kalshi.com"
		>
			<AlertTriangle className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
			{showLabel && 'Kalshi.com only'}
		</span>
	)
}

type NotTradableWarningProps = {
	platform: Platform
}

export function NotTradableWarning({ platform }: NotTradableWarningProps) {
	return (
		<div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-amber-400 text-xs">
			<AlertTriangle className="h-4 w-4 flex-shrink-0" />
			<div>
				<p className="font-medium">Not available for onchain trading</p>
				<p className="text-amber-400/80">
					Trade this market on{' '}
					<a
						href={platform === 'kalshi' ? 'https://kalshi.com' : 'https://polymarket.com'}
						target="_blank"
						rel="noopener noreferrer"
						className="underline hover:text-amber-300"
					>
						{platform === 'kalshi' ? 'Kalshi.com' : 'Polymarket.com'}
					</a>
				</p>
			</div>
		</div>
	)
}

type NewBadgeProps = {
	size?: 'sm' | 'md'
}

export function NewBadge({ size = 'sm' }: NewBadgeProps) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 rounded-full font-medium',
				'bg-violet-500/10 text-violet-400',
				size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'
			)}
		>
			<Sparkles className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
			New
		</span>
	)
}

type ArchivedBadgeProps = {
	size?: 'sm' | 'md'
}

export function ArchivedBadge({ size = 'sm' }: ArchivedBadgeProps) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 rounded-full font-medium',
				'bg-muted text-muted-foreground',
				size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'
			)}
		>
			<XCircle className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
			Archived
		</span>
	)
}
